import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PictureGrid.css';
import TickerTape from './TickerTape'; 
import advertConfig from './advertConfig';
import { logNewPlayerReward } from '../rewardsApi';

// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  PICTUREGRIDGAME.JSX - VERSION: 2026.01.18.1730                            ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2026.01.18.1730
// ============================================================================
// CHANGELOG:
// 2026.01.18.1730 - CLEANUP: Removed unused isNewPlayerBonusCelebration flag
// 2026.01.18.1710 - FIX: Center AdSense banner
// 2026.01.18.1700 - FIX: AdBanner triggers AdSense push after HTML renders
// 2026.01.18.1630 - FIX: AdBanner fetches from /api/global-adverts and renders HTML ads
// 2026.01.11.1200 - FIX: Show loading state instead of logo while picture loads
// 2025.12.23.0150 - FIX: Show TRAKtag logo instead of red square when no picture
// 2025.12.23.0145 - FIX: Picture loads when gamePictures arrives
// 2025.12.12.1845 - FIX: Improved sponsor banner styling with gradient background
// 2025.12.04.0002 - Added rewards logging for new player bonus
// 2025.12.04.0001 - FIX: New player bonus now checks DATABASE TRAKs to prevent repeated 50 TRAK bonus
// 2025.12.03.0002 - FIX: Increased bonus cell transition from 2s to 4s for readability
// 2025.12.03.0001 - FIX: Replaced innerHTML in AdBanner with React state to prevent DOM conflicts
// 2025.12.02.1840 - NEW PLAYER BONUS: Guaranteed 50 TRAKs on 2nd or 3rd click for first-time players
// 2025.12.02.1420 - Use central advertConfig for fallback ads
// 2025.12.02.1415 - Red "NO IMAGE" fallback instead of stock photos, use image_url from database
// 2025.12.01.0500 - Hide bonus values until cell is revealed (clicked)
// 2025.11.30.0205 - Replaced TT fallback with favicon in GameHeader
// 2025.11.28.0830 - Added logo placeholder to GameHeader
// 2025.11.28.0800 - Removed debug elements (V2.47, Grid Counter)
// ============================================================================

function PictureGridGame({ 
  onComplete, 
  onCellReveal, 
  onTrakEarned,
  viewMode = 'picture-on-black',  // 🔧 HARDCODED: Always picture-on-black
  gamePictures = [],
  adminMessage = '',
  playerData = null,
  traksInGame = 0,  // 🔧 FIX 2025.11.27.1800: Current session TRAKs from parent
  selectedGame = null,  // 🔧 FIX: Added for banner URL
  gridCount: gridCountProp = 0  // 🔧 FIX 2025.11.27: Receive gridCount from parent for picture rotation
} = {}) {
// 🎮 Main Game Component
// ============================================
// ✏️ UPDATED: Now receives pictures and settings from Game Admin
// - onComplete: Called after player clicks a cell (transitions to question)
//              Signature: onComplete(traks, isLastCell) 
//              🔧 FIX 2025.11.27.1615: isLastCell=true when this is the 16th cell
// - onCellReveal: Optional callback with cell value
// - onTrakEarned: Called immediately when bonus TRAKs are earned
// - viewMode: Always 'picture-on-black' (hardcoded - picture visible, cells turn black)
// - gamePictures: Array of pictures for this game (from Game Admin)
// - adminMessage: Optional message to display at top of grid
// - playerData: Player stats for displaying TRAKs and Streak 🔧 NEW
// - gridCount: Number of COMPLETED full grids (for picture rotation)
// ============================================

  // 🐛 DEBUG: Log received props
  useEffect(() => {
    console.log('========================================');
    console.log('🎮 PICTURE GRID COMPONENT MOUNTED/UPDATED');
    console.log('📸 gamePictures prop:', gamePictures);
    console.log('📸 Number of pictures:', gamePictures?.length || 0);
    console.log('👁️ viewMode:', viewMode);
    console.log('🎮 selectedGame:', selectedGame);
    console.log('📢 adminMessage (grid_message):', adminMessage);  // 🔧 FIX 2025.12.12: Debug sponsor banner
    console.log('🔢 gridCount PROP:', gridCount);  // 🔧 FIX 2025.11.27: Log the prop
    console.log('========================================');
    // 🛡️ ANTI-CRASH: Guard array access
    if (gamePictures && gamePictures.length > 0) {
      console.log('📸 First picture sample:', {
        game_id: gamePictures[0]?.game_id,
        pic_id: gamePictures[0]?.pic_id,
        active: gamePictures[0]?.active,
        file_path_preview: gamePictures[0]?.file_path?.substring(0, 50) + '...'
      });
    }
  }, [gamePictures, adminMessage]); // 🔧 DEBUG: Track when gamePictures or adminMessage changes

// === STATE MANAGEMENT ===
  // ✏️ REMOVED: customImage state (no longer uploading custom pictures)
  // ✏️ REMOVED: viewMode from state (now comes from props)
  
  // 🔧 FIX 2025.11.27.1600: Use gridCount from prop (passed from parent Section2WithIDLabels)
  const gridCount = gridCountProp;
  
  // 🔧 FIX 2025.11.27.1600: Derive isFirstGrid from prop instead of state
  // This fixes the bug where isFirstGrid stayed true because state reset on remount
  const isFirstGrid = gridCount === 0;
  
  // 🔧 FIX 2025.11.27.1600: Load pity counter from localStorage on init to prevent reset on remount
  const getInitialPityCounter = () => {
    try {
      const saved = localStorage.getItem('playerProgress');
      if (saved) {
        const data = JSON.parse(saved);
        console.log('📂 LOADING PITY COUNTER FROM STORAGE:', data.picturesSinceLast100 || 0);
        return data.picturesSinceLast100 || 0;
      }
    } catch (error) {
      console.error('Error loading pity counter:', error);
    }
    return 0;
  };

  const [gameState, setGameState] = useState({
    traks: playerData?.stats?.totalTraks || 0,  // 🔧 FIX: Use actual player TRAKs
    streak: playerData?.stats?.currentStreak || 0,  // 🔧 FIX: Use actual player streak
    picturesSinceLast100: getInitialPityCounter(),  // 🔧 FIX 2025.11.27.1600: Load from storage
    activeGift: null,
    availableBonuses: [50, 30, 20, 15, 10],
    currentTarget: 50,
    customMessage: '',
    currentPicture: null // Will be set from gamePictures
  });


  const [celebration, setCelebration] = useState({
    active: false,
    isPlayerGift: false,
    isTenBonus: false,
    isCommiseration: false
  });

  const [squares, setSquares] = useState([]);
  const resetInProgress = useRef(false);
  const [waitingForTransition, setWaitingForTransition] = useState(false); // ✏️ NEW: Block clicks during transition
  
  // 🆕 NEW PLAYER BONUS: Track clicks and bonus status for first-time players
  // 🔧 FIX 2025.12.04: Check BOTH localStorage AND database to prevent repeated bonuses
  const [newPlayerClickCount, setNewPlayerClickCount] = useState(() => {
    const saved = localStorage.getItem('newPlayerClickCount');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [newPlayerBonusClaimed, setNewPlayerBonusClaimed] = useState(() => {
    const claimed = localStorage.getItem('newPlayerBonusClaimed') === 'true';
    console.log('🎁 NEW PLAYER CHECK - newPlayerBonusClaimed from storage:', claimed);
    return claimed;
  });
  
  // 🔧 FIX: Player is NOT new if they have TRAKs in database OR localStorage flag is set
  const hasTraksInDatabase = (playerData?.stats?.totalTraks || 0) > 0;
  const hasCompletedGridBefore = localStorage.getItem('hasCompletedFirstGrid') === 'true';
  const isNewPlayer = !hasTraksInDatabase && !hasCompletedGridBefore && !newPlayerBonusClaimed;
  
  // 🔧 DEBUG: Log new player status on mount
  useEffect(() => {
    console.log('🎁 NEW PLAYER STATUS ON MOUNT:', {
      isNewPlayer,
      hasTraksInDatabase,
      playerTraks: playerData?.stats?.totalTraks || 0,
      newPlayerBonusClaimed,
      hasCompletedGridBefore,
      storageValues: {
        hasCompletedFirstGrid: localStorage.getItem('hasCompletedFirstGrid'),
        newPlayerBonusClaimed: localStorage.getItem('newPlayerBonusClaimed')
      }
    });
  }, [playerData]);

  // === CHECK FOR 10 TRAK GAMBLE STATE ===
  const shouldShowSlotEffect = useCallback(() => {
    const unrevealed = squares.filter(sq => !sq.revealed);
    const hasTen = unrevealed.some(sq => sq.bonusValue === 10);
    const hasHigherBonus = unrevealed.some(sq => sq.bonusValue > 10);
    const hasValidGambleCount = unrevealed.length >= 1 && unrevealed.length <= 5;
    return hasTen && !hasHigherBonus && hasValidGambleCount;
  }, [squares]);

  // === DEBUG: CHECK SQUARES STATE ===
  useEffect(() => {
    const revealedCount = squares.filter(square => square.revealed).length;
    const totalSquares = squares.length;
    console.log(`Squares: ${revealedCount}/${totalSquares} revealed`, {
      allRevealed: squares.length > 0 && squares.every(square => square.revealed),
      picturesSinceLast100: gameState.picturesSinceLast100,
      hasBonus: squares.some(sq => sq.isBonus),
      shouldShowSlot: shouldShowSlotEffect()
    });
  }, [squares, gameState.picturesSinceLast100, shouldShowSlotEffect]);

  // === INITIALIZE GRID WITH PERSISTENCE ===
  useEffect(() => {
    // Reset transition lock when grid appears
    setWaitingForTransition(false);
    
    // ============================================
    // ✏️ NEW: Try to load saved grid first
    // ============================================
    const savedGrid = localStorage.getItem('currentPictureGrid');
    
    if (savedGrid) {
      try {
        const { squares: savedSquares, gameState: savedGameState } = JSON.parse(savedGrid);
        console.log('📂 LOADING SAVED GRID - Cells revealed:', savedSquares.filter(sq => sq.revealed).length);
        setSquares(savedSquares);
        setGameState(prev => ({ ...prev, ...savedGameState }));
      } catch (error) {
        console.error('Error loading saved grid:', error);
        initializeGrid(); // Fallback to new grid
      }
    } else {
      console.log('🆕 NO SAVED GRID - Initializing new grid');
      initializeGrid();
    }
    
    loadPlayerProgress();
  }, []);
  
  // 🆕 NEW: Select picture based on sequence
  // 🛡️ ANTI-CRASH: Added guards for array access
  // 🔧 FIX: Removed gridCount from dependencies to prevent mid-game re-renders
  const selectPictureForGrid = useCallback(() => {
    console.log('========================================');
    console.log('🎯 SELECTING PICTURE FOR GRID');
    console.log('Total gamePictures:', gamePictures?.length || 0);
    console.log('isFirstGrid:', isFirstGrid);
    console.log('gridCount:', gridCount);
    
    if (!gamePictures || gamePictures.length === 0) {
      console.log('⚠️ No pictures - using red placeholder');
      return null; // Return null to trigger red "NO IMAGE" state
    }
    
    // Filter active pictures only
    const activePictures = gamePictures.filter(pic => {
      // Check if picture is active
      if (!pic?.active) {
        console.log(`❌ Picture ${pic?.picture_id} filtered out: not active`);
        return false;
      }
      
      // Check date range
      const now = new Date();
      if (pic.start_date && new Date(pic.start_date) > now) {
        console.log(`❌ Picture ${pic.picture_id} filtered out: start date not reached`);
        return false;
      }
      if (pic.end_date && new Date(pic.end_date) < now) {
        console.log(`❌ Picture ${pic.picture_id} filtered out: end date passed`);
        return false;
      }
      
      // Check impression limit
      if (pic.max_impressions && pic.current_impressions >= pic.max_impressions) {
        console.log(`❌ Picture ${pic.picture_id} filtered out: impression limit reached`);
        return false;
      }
      
      console.log(`✅ Picture ${pic.picture_id} is active and available`);
      return true;
    }).sort((a, b) => (a?.display_order || 0) - (b?.display_order || 0));
    
    console.log('Active pictures after filtering:', activePictures.length);
    console.log('Active pictures:', activePictures.map(p => ({ 
      picture_id: p.picture_id, 
      display_order: p.display_order,
      image_url: (p.image_url || p.file_path)?.substring(0, 50) + '...'
    })));
    
    if (activePictures.length === 0) {
      console.log('⚠️ No active pictures - using red placeholder');
      return null; // Return null to trigger red "NO IMAGE" state
    }
    
    // First grid of session - use first picture (start picture)
    if (isFirstGrid) {
      console.log('🎬 FIRST GRID - Using picture at index 0');
      console.log('Selected picture ID:', activePictures[0]?.picture_id);
      // 🔧 FIX 2025.11.27.1600: Removed setIsFirstGrid - now derived from gridCount prop
      // 🛡️ ANTI-CRASH: Safe access with optional chaining
      return activePictures[0]?.image_url || activePictures[0]?.file_path || null;
    }
    
    // Subsequent grids - rotate through all pictures (including first)
    // 🛡️ ANTI-CRASH: Ensure pictureIndex is valid
    // 🔧 FIX: Use gridCount from closure, not from dependency
    const pictureIndex = activePictures.length > 0 ? gridCount % activePictures.length : 0;
    console.log('🔄 SUBSEQUENT GRID - Rotation logic');
    console.log('Calculation: gridCount % activePictures.length =', gridCount, '%', activePictures.length, '=', pictureIndex);
    console.log('Selected picture ID:', activePictures[pictureIndex]?.picture_id);
    console.log('Selected picture url:', (activePictures[pictureIndex]?.image_url || activePictures[pictureIndex]?.file_path)?.substring(0, 50) + '...');
    console.log('========================================');
    return activePictures[pictureIndex]?.image_url || activePictures[pictureIndex]?.file_path || null;
  }, [gamePictures, isFirstGrid]); // 🔧 FIX: Removed gridCount from dependencies
  
  // 🔧 FIX 2025.11.27: Track gridCount prop to prevent picture change mid-game
  const lastGridCountRef = useRef(gridCount);
  const lastPicturesLengthRef = useRef(0);  // 🆕 Track when pictures load
  
  // Set current picture ONLY when gridCount changes OR when gamePictures first loads
  useEffect(() => {
    const picturesLength = gamePictures?.length || 0;
    const picturesJustLoaded = lastPicturesLengthRef.current === 0 && picturesLength > 0;
    
    // 🔧 FIX 2025.11.27: Only update picture when gridCount actually changes
    console.log('🔄 gridCount PROP CHECK:', { gridCountProp: gridCount, lastRef: lastGridCountRef.current, picturesLength, picturesJustLoaded });
    
    if (lastGridCountRef.current !== gridCount || gameState.currentPicture === null || picturesJustLoaded) {
      console.log('========================================');
      console.log('🖼️ PICTURE CHANGE TRIGGERED');
      console.log('Reason:', 
        picturesJustLoaded ? '🆕 Pictures just loaded' :
        lastGridCountRef.current !== gridCount ? '✅ gridCount changed' : '⚠️ currentPicture is null');
      console.log('Old gridCount:', lastGridCountRef.current);
      console.log('New gridCount:', gridCount);
      console.log('Current picturesSinceLast100:', gameState.picturesSinceLast100);
      console.log('========================================');
      
      lastGridCountRef.current = gridCount;
      lastPicturesLengthRef.current = picturesLength;
      const picture = selectPictureForGrid();
      console.log('🖼️ NEW PICTURE SELECTED (grid #' + gridCount + '):', {
        pictureUrl: picture?.substring(0, 50) + '...', // Show first 50 chars
        totalGamePictures: gamePictures?.length || 0,
        activePicturesCount: gamePictures?.filter(p => p?.active).length || 0,
        isFirstGrid,
        gridCount
      });
      setGameState(prev => ({ ...prev, currentPicture: picture }));
    }
    // 🔧 FIX 2025.12.23: Added gamePictures?.length to dependencies so picture loads when data arrives
  }, [gridCount, gamePictures?.length]); // 🔧 FIX: Re-run when pictures load
  
  // 🔧 FIX: Accept optional parameter to fix state timing issue for pity 50 TRAK
  const initializeGrid = (overridePicturesSinceLast = null) => {
    // 🔧 FIX: Use passed value if provided (to handle async state updates)
    const currentPicturesSinceLast = overridePicturesSinceLast !== null 
      ? overridePicturesSinceLast 
      : gameState.picturesSinceLast100;
    const shouldAddBonusCell = currentPicturesSinceLast >= 5;
    
    console.log('========================================');
    console.log('🔄 INITIALIZING GRID');
    console.log('Pictures since last 50:', currentPicturesSinceLast);
    console.log('Override value passed:', overridePicturesSinceLast);
    console.log('Should add bonus cell:', shouldAddBonusCell, '(>= 5)');
    console.log('========================================');
    
    // (rest of your existing initializeGrid code continues here...)

    // ============================================
    // ✏️ UPDATED: Treasure values 50/30/20/15/10
    // ============================================
    setGameState(prev => ({
      ...prev,
      availableBonuses: [50, 30, 20, 15, 10],
      currentTarget: 50
      // currentPicture is set by selectPictureForGrid useEffect - don't override it here
    }));

    const bonusValues = [50, 30, 20, 15, 10, ...Array(11).fill(0)]; // 4x4 = 16 cells
    const shuffledBonuses = [...bonusValues].sort(() => Math.random() - 0.5);
    
    let newSquares = shuffledBonuses.map((bonusValue, index) => ({
      id: index,
      bonusValue,
      revealed: false,
      active: true,
      isBonus: false
    }));

    if (shouldAddBonusCell) {
      console.log('🎁 BONUS CELL LOGIC ACTIVATED');
      console.log('Current grid before bonus:', newSquares.map(sq => sq.bonusValue));
      
      // ============================================
      // ✏️ UPDATED: Bonus cell now 50 instead of 100
      // ============================================
      const non50Squares = newSquares.filter(sq => sq.bonusValue !== 50);
      console.log('Non-50 squares found:', non50Squares.length);
      
      if (non50Squares.length > 0) {
        const randomIndex = Math.floor(Math.random() * non50Squares.length);
        const bonusSquareId = non50Squares[randomIndex].id;
        
        console.log('Selected square ID for bonus:', bonusSquareId);
        console.log('Original value:', newSquares[bonusSquareId].bonusValue);
        
        newSquares = newSquares.map(square => 
          square.id === bonusSquareId 
            ? { ...square, bonusValue: 50, isBonus: true }
            : square
        );
        
        const bonusCell = newSquares.find(sq => sq.isBonus);
        console.log('✅ BONUS CELL ADDED at position:', bonusSquareId);
        console.log('Bonus cell verification:', bonusCell);
        console.log('Grid after bonus:', newSquares.map(sq => ({ id: sq.id, value: sq.bonusValue, isBonus: sq.isBonus })));
        
        setGameState(prev => ({
          ...prev,
          picturesSinceLast100: 0
        }));
        
        console.log('Counter reset to 0 after bonus cell added');
      } else {
        console.log('⚠️ WARNING: No non-50 squares available for bonus cell!');
      }
    } else {
      console.log('⏭️ No bonus cell added (count not reached 5)');
    }
    
    setSquares(newSquares);
  };

// === DETECT WHEN ALL CELLS ARE CLICKED ===
  useEffect(() => {
    const allRevealed = squares.length > 0 && squares.every(square => square.revealed);
    
    if (allRevealed) {
      console.log('✅ All cells revealed - incrementing counter NOW');
      
      // 🔧 FIX: Increment counter IMMEDIATELY (not in setTimeout)
      // This ensures counter updates even if component unmounts during transition
      const newPicturesSinceLast = gameState.picturesSinceLast100 + 1;
      
      setGameState(prev => ({
        ...prev,
        picturesSinceLast100: prev.picturesSinceLast100 + 1
      }));
      
      console.log('📊 Counter incremented:', gameState.picturesSinceLast100, '→', newPicturesSinceLast);
      
      // Delay only for grid initialization and picture change
      const timer = setTimeout(() => {
        console.log('🔄 2 seconds passed - loading new picture');
        
        // 🔧 FIX 2025.11.27: Removed setGridCount - parent now passes gridCount as prop
        // The parent (Section2WithIDLabels) increments completedGridCount in handleGridComplete
        
        // 🔧 FIX: Pass the NEW value to initializeGrid to fix pity 50 TRAK timing
        initializeGrid(newPicturesSinceLast);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [squares]);

  // === PERSISTENCE ===
  const loadPlayerProgress = () => {
    try {
      const saved = localStorage.getItem('playerProgress');
      if (saved) {
        const data = JSON.parse(saved);
        setGameState(prev => ({
          ...prev,
          traks: data.traks || 0,
          streak: data.streak || 0,
          picturesSinceLast100: data.picturesSinceLast100 || 0
        }));
      }
    } catch (error) {
      console.error('Error loading player progress:', error);
    }
  };

  const savePlayerProgress = useCallback(() => {
    try {
      const data = {
        traks: gameState.traks,
        streak: gameState.streak,
        picturesSinceLast100: gameState.picturesSinceLast100,
        lastUpdated: Date.now()
      };
      localStorage.setItem('playerProgress', JSON.stringify(data));
    } catch (error) {
      console.error('Error saving player progress:', error);
    }
  }, [gameState.traks, gameState.streak, gameState.picturesSinceLast100]);

  // 🔧 FIX 2025.11.27.1630: Auto-save picturesSinceLast100 whenever it changes
  useEffect(() => {
    // Only save if we have a valid value (not initial render with 0)
    if (gameState.picturesSinceLast100 >= 0) {
      console.log('💾 AUTO-SAVING pity counter:', gameState.picturesSinceLast100);
      savePlayerProgress();
    }
  }, [gameState.picturesSinceLast100, savePlayerProgress]);

  // === TRAK MANAGEMENT ===
  const addTraks = useCallback((amount, source = 'gameplay') => {
    setGameState(prev => ({
      ...prev,
      traks: prev.traks + amount
    }));
  }, []);

  // === BONUS TRACKER MANAGEMENT ===
  const updateBonusTracker = useCallback((clickedValue) => {
    console.log('🎯 UPDATE BONUS TRACKER - Clicked:', clickedValue, 'Current Target:', gameState.currentTarget, 'Available:', gameState.availableBonuses);
    
    if (!gameState.currentTarget || gameState.availableBonuses.length === 0) {
      return;
    }
    
    if (clickedValue === 10) {
      console.log('🎉 10 TRAK FOUND - HIDING TRACKER COMPLETELY');
      
      setSquares(prev => prev.map(square => {
        if (square.bonusValue > 10 && !square.revealed && !square.isBonus) {
          console.log('❌ Resetting higher square:', square.id, 'from', square.bonusValue, 'to 0');
          return {
            ...square,
            bonusValue: 0,
            active: false
          };
        }
        return square;
      }));
      
      setGameState(prev => ({
        ...prev,
        availableBonuses: [],
        currentTarget: null
      }));
      return;
    }
    
    if (clickedValue === gameState.currentTarget) {
      const currentIndex = gameState.availableBonuses.indexOf(clickedValue);
      const nextTarget = gameState.availableBonuses[currentIndex + 1];
      
      console.log('✅ Correct order - Current index:', currentIndex, 'Next target:', nextTarget);
      
      if (nextTarget) {
        const newAvailableBonuses = gameState.availableBonuses.slice(currentIndex + 1);
        
        setGameState(prev => ({
          ...prev,
          availableBonuses: newAvailableBonuses,
          currentTarget: nextTarget
        }));
      } else {
        setGameState(prev => ({
          ...prev,
          availableBonuses: [],
          currentTarget: null
        }));
      }
    } else if (gameState.availableBonuses.includes(clickedValue)) {
      const clickedIndex = gameState.availableBonuses.indexOf(clickedValue);
      
      console.log('🔄 Out-of-sequence click - Starting new sequence from next value after:', clickedValue);
      
      const newBonuses = gameState.availableBonuses.slice(clickedIndex + 1);
      
      if (newBonuses.length > 0) {
        setGameState(prev => ({
          ...prev,
          availableBonuses: newBonuses,
          currentTarget: newBonuses[0]
        }));

        resetHigherValueSquares(clickedValue, newBonuses);
      } else {
        setGameState(prev => ({
          ...prev,
          availableBonuses: [],
          currentTarget: null
        }));
      }
    }
  }, [gameState.currentTarget, gameState.availableBonuses]);

  // === RESET HIGHER VALUE SQUARES ===
  const resetHigherValueSquares = (clickedValue, newBonuses) => {
    if (resetInProgress.current) return;
    resetInProgress.current = true;
    
    console.log('🔁 RESETTING HIGHER VALUE SQUARES - Clicked:', clickedValue, 'New Bonuses:', newBonuses);
    
    setSquares(prev => {
      const newSquares = prev.map(square => {
        if (square.bonusValue > clickedValue && 
            !newBonuses.includes(square.bonusValue) && 
            !square.revealed &&
            !square.isBonus) {
          console.log('❌ Resetting square:', square.id, 'from', square.bonusValue, 'to 0');
          return {
            ...square,
            bonusValue: 0,
            active: false
          };
        }
        return square;
      });
      
      resetInProgress.current = false;
      return newSquares;
    });
  };

  // === GLOW INTENSITY SYSTEM ===
  const getGlowClass = useCallback((square) => {
    if (square.revealed) return '';
    
    const unrevealedCount = squares.filter(sq => !sq.revealed).length;
    const has15TrakBeenFound = squares.some(sq => sq.revealed && sq.bonusValue === 15); // ✏️ UPDATED: Changed from 25 to 15
    const isFinalGamble = gameState.currentTarget === 10 && unrevealedCount <= 5;
    
    if (!has15TrakBeenFound || !isFinalGamble) return '';
    
    if (unrevealedCount === 5) return 'glow-subtle';
    if (unrevealedCount === 4) return 'glow-medium';
    if (unrevealedCount === 3) return 'glow-bright';
    if (unrevealedCount <= 2) return 'glow-intense-pulse';
    
    return '';
  }, [squares, gameState.currentTarget]);

  // === VIEW MODE TOGGLE ===
  // ✏️ REMOVED: toggleViewMode function (viewMode now controlled by Game Admin)

  // === IMAGE UPLOAD ===
     // === GRID SQUARES RENDERER ===
const renderGridSquares = () => {
  return squares.map((square, index) => {
    return (
      <div
        key={square.id}
        className={`grid-square ${square.revealed ? 'revealed' : 'hidden'} ${
          !square.active ? 'inactive' : ''
        } ${square.isBonus ? 'bonus-cell' : ''} ${
          shouldShowSlotEffect() && !square.revealed ? 'gamble-active' : ''
        } ${getGlowClass(square)}`}
        onClick={(e) => handleSquareClick(index, e.currentTarget)}
      >
        <div 
          className="trak-value-display"
          data-value={square.bonusValue}
          style={{ opacity: square.revealed ? 1 : 0 }}
        >
          {square.bonusValue > 0 ? square.bonusValue : '0'}
        </div>
        
        {square.isBonus && !square.revealed && (
          <div className="bonus-label">BONUS</div>
        )}
        
        {shouldShowSlotEffect() && !square.revealed && (
          <div className="gamble-label">?</div>
        )}
        
        {/* Always render black-overlay - CSS controls opacity based on mode and revealed state */}
        <div className="black-overlay" />
      </div>
    );
  });
};

  // === SQUARE CLICK HANDLER ===
  const handleSquareClick = useCallback((index, element) => {
    const square = squares[index];
    
    if (square.revealed) {
      console.log('⚠️ Cell already revealed, ignoring click');
      return;
    }

    // ============================================
    // ✏️ NEW: Block clicks if waiting for transition
    // ============================================
    if (waitingForTransition) {
      console.log('⚠️ Waiting for transition - click blocked');
      return;
    }

    console.log('🖱️ CLICKED SQUARE:', index, 'Value:', square.bonusValue, 'IsBonus:', square.isBonus);
    console.log('📊 Current state - Revealed cells:', squares.filter(sq => sq.revealed).length, '/16');

    // 🔧 FIX 2025.11.27.1615: Calculate if this is the LAST cell BEFORE state update
    // This determines if picture should rotate (only after full 16-cell grid)
    const currentRevealedCount = squares.filter(sq => sq.revealed).length;
    const isLastCell = currentRevealedCount === 15; // This click will be the 16th
    console.log('🔢 Is this the last cell?', isLastCell, '(revealed:', currentRevealedCount, '+ 1 = 16?)');

    // Block further clicks immediately
    setWaitingForTransition(true);

    setSquares(prev => prev.map((s, i) => 
      i === index ? { ...s, revealed: true } : s
    ));

    // 🆕 NEW PLAYER BONUS: Give 50 TRAKs on 2nd or 3rd click for new players
    let bonusValue = square.bonusValue;
    const clickNumber = newPlayerClickCount + 1;
    setNewPlayerClickCount(clickNumber);
    localStorage.setItem('newPlayerClickCount', clickNumber.toString());
    
    // 🆕 NEW PLAYER BONUS: Give 50 TRAKs on 2nd or 3rd click for new players
    // But if they naturally hit 50 on any click, mark bonus as claimed
    if (isNewPlayer && !newPlayerBonusClaimed) {
      if (bonusValue >= 50) {
        // They got 50 naturally - mark as claimed so no extra bonus needed
        console.log('🎁 NEW PLAYER: Got 50 naturally on click', clickNumber, '- marking bonus as claimed');
        setNewPlayerBonusClaimed(true);
        localStorage.setItem('newPlayerBonusClaimed', 'true');
      } else if (clickNumber >= 2 && clickNumber <= 3) {
        // Override to 50 on 2nd or 3rd click
        console.log('🎁 NEW PLAYER BONUS! Overriding value from', bonusValue, 'to 50 on click', clickNumber);
        bonusValue = 50;
        setNewPlayerBonusClaimed(true);
        localStorage.setItem('newPlayerBonusClaimed', 'true');
        
      // 🔧 FIX 2025.12.04: Log new player bonus to database
        logNewPlayerReward(50);
        
        // Update the square's display value
        setSquares(prev => prev.map((s, i) => 
          i === index ? { ...s, bonusValue: 50 } : s
        ));
      }
    }
    
    const isTenTrak = bonusValue === 10;
    const wasGamble = shouldShowSlotEffect();
    const unrevealedCount = squares.filter(sq => !sq.revealed).length;
    const hasTenTrakRemaining = squares.some(sq => !sq.revealed && sq.bonusValue === 10);
    const isFinalGamble = gameState.currentTarget === 10 && unrevealedCount <= 5 && hasTenTrakRemaining;
    
    if (isFinalGamble && bonusValue === 0) {
      console.log('💔 WRONG CELL IN FINAL GAMBLE - 10 TRAK LOST');
      console.log('🎭 SETTING COMMISERATION BUBBLE');
      
      playChampagneCelebration(false, false, true);

      setSquares(prev => prev.map(sq => 
        sq.bonusValue === 10 && !sq.revealed 
          ? { ...sq, bonusValue: 0, active: false }
          : sq
      ));
      
      setGameState(prev => ({
        ...prev,
        availableBonuses: [],
        currentTarget: null
      }));
      
      savePlayerProgress();
      
      // ============================================
      // ✏️ Transition after gamble loss - pass 0 TRAKs
      // 🔧 FIX 2025.11.27.1615: Pass isLastCell flag
      // ============================================
      setTimeout(() => {
        if (onComplete && typeof onComplete === 'function') {
          console.log('🎯 AUTO-TRANSITION after gamble loss - moving to question');
          console.log('💰 Passing TRAKs to parent: 0 (gamble loss), isLastCell:', isLastCell);
          
          setWaitingForTransition(false);
          onComplete(0, isLastCell); // 🔧 FIX: Pass isLastCell flag
        }
      }, 2000);
      
      return;
    }
    
    if (bonusValue > 0) {
      console.log('💰 BONUS TRAKs EARNED:', bonusValue, 'from square:', square.id);
      console.log('🔍 Square details:', { 
        originalBonusValue: square.bonusValue, 
        currentBonusValue: bonusValue,
        isBonus: square.isBonus 
      });
      
      // Store locally to pass after timeout
      const traksToPass = bonusValue;
      console.log('📦 traksToPass stored as:', traksToPass);
      
      // 🔧 FIX 2025.11.27.1755: Don't update tracker for bonus cells - they're separate from sequence
      if (square.isBonus) {
        console.log('🎁 This is a BONUS cell - not updating tracker (regular 50 still available)');
      } else if (isFinalGamble && isTenTrak) {
        setGameState(prev => ({
          ...prev,
          customMessage: 'CONGRATULATIONS YOU WON 10 TRAK BONUS'
        }));
        playChampagneCelebration(false, true);
        
        updateBonusTracker(bonusValue);
      } else {
        updateBonusTracker(bonusValue);
      }
    
      if (bonusValue === 50) { // ✏️ UPDATED: Changed from 100 to 50
        if (square.isBonus) {
          setGameState(prev => ({
            ...prev,
            customMessage: '🎁 SPECIAL BONUS AWARD! 🥂\n+50 TRAKS!', // ✏️ UPDATED: Changed from 100 to 50
            picturesSinceLast100: 0
          }));
          playChampagneCelebration(true, false);
        } else {
          playChampagneCelebration(false, false);
          setGameState(prev => ({
            ...prev,
            picturesSinceLast100: 0,
            customMessage: ''
          }));
        }
      } else if (isTenTrak && wasGamble && !isFinalGamble) {
        setGameState(prev => ({
          ...prev,
          customMessage: '🎰 JACKPOT! +10 TRAKS!'
        }));
        playChampagneCelebration(false, true);
      }
      
      // ============================================
      // ✏️ PASS TRAKs IN TIMEOUT USING LOCAL VARIABLE
      // 🔧 FIX 2025.12.03: Increased from 2000ms to 4000ms so players can read celebration
      // ============================================
      console.log('⏱️ Setting 4-second transition timer, onComplete available:', typeof onComplete === 'function');
      console.log('💰 traksToPass prepared:', traksToPass);
      setTimeout(() => {
        console.log('⏰ 4 seconds elapsed! Checking onComplete:', typeof onComplete === 'function');
        if (onComplete && typeof onComplete === 'function') {
          console.log('🎯 AUTO-TRANSITION - 4 seconds elapsed, moving to question');
          console.log('💰 CALLING onComplete with TRAKs:', traksToPass, 'isLastCell:', isLastCell);
          
          setWaitingForTransition(false); // Reset for next time
          onComplete(traksToPass, isLastCell); // 🔧 FIX 2025.11.27.1615: Pass isLastCell flag
          console.log('✅ onComplete CALLED successfully');
        } else {
          console.log('❌ onComplete not available - TRAKs LOST! Cannot transition!');
        }
      }, 4000);
      
      return; // Exit early since we set timeout above
    } else if (wasGamble && !isFinalGamble) {
      setGameState(prev => ({
        ...prev,
        customMessage: '💫 SO CLOSE! Try again!'
      }));
      setTimeout(() => {
        setGameState(prev => ({ ...prev, customMessage: '' }));
      }, 1500);
    } else {
      setGameState(prev => ({ ...prev, customMessage: '' }));
    }

    savePlayerProgress();
    
    // ============================================
    // ✏️ Grid will be saved by useEffect after state updates
    // ============================================

    // ============================================
    // ✏️ AUTO-TRANSITION for NON-BONUS cells (bonusValue === 0)
    // Bonus cells handle their own timeout above
    // 🔧 FIX 2025.11.27.1615: Pass isLastCell flag
    // ============================================
    if (bonusValue === 0) {
      console.log('⏱️ Setting 2-second transition timer for non-bonus cell');
      setTimeout(() => {
        console.log('⏰ 2 seconds elapsed! Moving to question, isLastCell:', isLastCell);
        if (onComplete && typeof onComplete === 'function') {
          setWaitingForTransition(false);
          onComplete(0, isLastCell); // 🔧 FIX: Pass isLastCell flag
        }
      }, 2000);
    }
    
  }, [squares, addTraks, updateBonusTracker, shouldShowSlotEffect, savePlayerProgress, gameState.currentTarget, waitingForTransition, onComplete]);

  // === SAVE GRID AFTER STATE UPDATES ===
  useEffect(() => {
    if (squares.length === 0) return; // Don't save empty grid
    
    // Save current grid to localStorage AFTER all updates
    localStorage.setItem('currentPictureGrid', JSON.stringify({
      squares: squares,
      gameState: gameState
    }));
    
    const revealedCount = squares.filter(sq => sq.revealed).length;
    console.log('💾 GRID SAVED - Revealed:', revealedCount, '/16');
    
    // Check if all 16 cells revealed
    if (squares.every(sq => sq.revealed) && squares.length === 16) {
      console.log('🎉 ALL 16 CELLS REVEALED - Clearing grid for new one');
      localStorage.removeItem('currentPictureGrid');
      
      // 🆕 Mark that player has completed their first grid (for new player bonus logic)
      if (!localStorage.getItem('hasCompletedFirstGrid')) {
        localStorage.setItem('hasCompletedFirstGrid', 'true');
        console.log('🎓 NEW PLAYER: First grid completed - future grids will use normal logic');
      }
    }
  }, [squares, gameState]); // Runs whenever squares or gameState changes

  // === CELEBRATION SYSTEMS ===
  const playChampagneCelebration = (isPlayerGift = false, isTenBonus = false, isCommiseration = false) => {
    console.log('🎭 playChampagneCelebration called:', { isPlayerGift, isTenBonus, isCommiseration });
    
    setCelebration({
      active: true,
      isPlayerGift,
      isTenBonus,
      isCommiseration
    });

    // 🔧 FIX 2025.11.28: Increased from 2000ms to 4000ms to allow celebration to display properly
    // Must be longer than ChampagneCelebration's internal timer (2500ms + buffer)
    setTimeout(() => {
      console.log('🎭 Clearing celebration bubble (4s elapsed)');
      setCelebration({ active: false, isPlayerGift: false, isTenBonus: false, isCommiseration: false });
    }, 4000);
  };

  // Debug glow system
  useEffect(() => {
    const unrevealedCount = squares.filter(sq => !sq.revealed).length;
    const has15TrakBeenFound = squares.some(sq => sq.revealed && sq.bonusValue === 15); // ✏️ UPDATED: Changed from 25 to 15
    const isFinalGamble = gameState.currentTarget === 10 && unrevealedCount <= 5;
    
    if (has15TrakBeenFound && isFinalGamble) {
      console.log('🎯 GLOW SYSTEM ACTIVE - Cells left:', unrevealedCount, 'Glow level:', 
        unrevealedCount === 5 ? 'subtle' :
        unrevealedCount === 4 ? 'medium' :
        unrevealedCount === 3 ? 'bright' : 'intense-pulse'
      );
    }
  }, [squares, gameState.currentTarget]);

  // 🔧 FIX 2026.01.11: Show loading state while picture is being selected
  // This prevents the TRAKtag logo from flashing before the sponsor picture loads
  if (gamePictures && gamePictures.length > 0 && !gameState.currentPicture) {
    return (
      <div className="picture-grid-game" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'pulse 1.5s infinite' }}>🖼️</div>
          <p style={{ color: 'white', fontSize: '16px', marginBottom: '8px' }}>Loading picture grid...</p>
          <p style={{ color: '#9CA3AF', fontSize: '14px' }}>{selectedGame?.name || 'Quiz'}</p>
        </div>
      </div>
    );
  }

  // === RENDER ===
  return (
  <div className="picture-grid-game">

    {/* 1. TRAKs & Streak Banner - same width as grid (340px) */}
    {/* 🔧 FIX 2025.11.27.1800: Show total = saved + current session TRAKs */}
    <GameHeader 
      traks={(playerData?.stats?.totalTraks || 0) + traksInGame}
      streak={playerData?.stats?.currentStreak || 0}
    />

    {/* 2. Ticker Tape - ALMOST TOUCHING header, same width as grid */}
    <div style={{ marginTop: '1px', width: '100%', maxWidth: '340px' }}>
      <TickerTape />
    </div>

    {/* 3. Sponsor Message Banner - Per-game (from selectedGame.grid_message) */}
    {adminMessage && (
      <div 
        className="admin-message-banner" 
        style={{ 
          marginTop: '4px', 
          marginBottom: '4px',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 'bold',
          textAlign: 'center',
          width: '100%',
          maxWidth: '340px',
          boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
        }}
      >
        📢 {adminMessage}
      </div>
    )}

    {/* 4. Message Banner - ALMOST TOUCHING ticker/sponsor */}
    <div style={{ marginTop: '1px' }}>
      <CustomMessageArea 
        message={gameState.customMessage}
      />
    </div>

      {/* 🔧 FIX 2025.11.27.1745: Reduced marginTop to 4px to match bottom gap */}
      <div className="grid-wrapper" style={{ marginTop: '4px', marginBottom: '0' }}>

        <div className="control-row">
          {/* ✏️ REMOVED: View mode toggle button */}
          {/* ✏️ REMOVED: Upload custom picture button */}

          {/* 🔧 FIX 2025.11.27.1750: Show instruction before first click, then bonus tracker */}
          {squares.filter(sq => sq.revealed).length === 0 ? (
            <div className="start-instruction">
              👆 Click on picture to start quiz
            </div>
          ) : (
            gameState.availableBonuses && gameState.availableBonuses.length > 0 && gameState.currentTarget && (
              <BonusTracker 
                availableBonuses={gameState.availableBonuses}
                currentTarget={gameState.currentTarget}
                squares={squares}
              />
            )
          )}
        </div>

        {/* 5. Picture Grid - with equal spacing top and bottom */}
        <div 
  className={`grid-container ${viewMode}`}
  style={{ 
    backgroundImage: gameState.currentPicture 
      ? `url(${gameState.currentPicture})` 
      : 'url(/TRAKtag_Logo_Full.png)',  // 🔧 FIX 2025.12.23: Show TRAKtag logo instead of red square
    backgroundSize: gameState.currentPicture ? 'cover' : 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#1f2937'  // Dark gray background behind logo
  }}
>
  {/* Grid squares render over the background (logo or sponsor picture) */}
  {renderGridSquares()}
</div>

        {celebration.active && (
          <div className="champagne-celebration" style={{ zIndex: 9999 }}>
            <ChampagneCelebration 
              isPlayerGift={celebration.isPlayerGift}
              isTenBonus={celebration.isTenBonus}
              isCommiseration={celebration.isCommiseration}
            />
          </div>
        )}
      </div>
      
      {/* 6. Bottom Ad Banner - same width as grid, small gap */}
      <div style={{ width: '100%', maxWidth: '340px', margin: '0 auto' }}>
        <AdBanner 
          selectedGame={selectedGame}
          squares={squares}
        />
      </div>
    </div>
  );
};

// Header Component with Logo
// 🔧 FIX 2025.11.28: Added logo placeholder (50x50)
const GameHeader = ({ traks, streak, logoUrl }) => {
  return (
    <div className="game-header">
      {/* Logo placeholder - 50x50 */}
      <div className="header-logo">
        {logoUrl ? (
          <img src={logoUrl} alt="TRAKtag" className="logo-img" />
        ) : (
          <img src="/favicon-48.png" alt="TRAKtag" className="logo-img" />
        )}
      </div>
      <div className="trak-counter">
        🏆 TRAKs: <span className="trak-count">{traks.toLocaleString()}</span>
      </div>
      <div className="streak-counter">
        🔥 Streak: <span className="streak-count">{streak}</span> days
      </div>
    </div>
  );
};

// Gift Progress Indicator Component
// Gift Progress Indicator Component
// 🔧 DEBUG: Added manual test buttons for development
const GiftProgressIndicator = ({ picturesSinceLast100, onManualIncrement, onManualReset }) => {
  const isApproaching = picturesSinceLast100 >= 3 && picturesSinceLast100 < 5;
  const isGuaranteed = picturesSinceLast100 >= 5;

  let className = 'gift-progress';
  let content = '';

  if (isGuaranteed) {
    className += ' gift-active';
    content = '🎉 BONUS CELL ACTIVATED!';
  } else if (isApproaching) {
    className += ' gift-approaching';
    content = `⭐ Bonus Progress\n${picturesSinceLast100}/5 pictures`;
  } else {
    content = `⭐ Bonus Progress\n${picturesSinceLast100}/5 pictures`;
  }

  return (
    <div className={className}>
      {content.split('\n').map((line, index) => (
        <div key={index}>{line}</div>
      ))}
      {/* 🔧 DEBUG: Test buttons - remove for production */}
      <div style={{display: 'flex', gap: '4px', marginTop: '4px'}}>
        <button 
          onClick={onManualIncrement}
          style={{fontSize: '10px', padding: '2px 6px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer'}}
        >
          +1 Test
        </button>
        <button 
          onClick={onManualReset}
          style={{fontSize: '10px', padding: '2px 6px', background: '#f44336', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer'}}
        >
          Reset
        </button>
      </div>
    </div>
  );
};


// Custom Message Area Component
const CustomMessageArea = ({ message }) => {
  console.log('📢 CustomMessageArea rendering with message:', message);
  if (!message) return null;
  
  return (
    <div className="custom-message">
      {message}
    </div>
  );
};

// Bonus Tracker Component
const BonusTracker = ({ availableBonuses, currentTarget, squares }) => {
  if (!availableBonuses || availableBonuses.length === 0 || !currentTarget) {
    return null;
  }

  const unrevealedCount = squares.filter(sq => !sq.revealed).length;
  const hasTenTrakRemaining = squares.some(sq => !sq.revealed && sq.bonusValue === 10);
  const isFinalGamble = currentTarget === 10 && unrevealedCount <= 5 && hasTenTrakRemaining;

  let title = '';

  // ============================================
  // ✏️ UPDATED: Changed all references to new values (50/30/20/15/10)
  // ============================================
  if (currentTarget === 50) {
    title = '🔥 STILL 50 TRAK BONUS TO FIND';
  } else if (currentTarget === 30) {
    title = '🔥 Find Bonus TRAKs in order: 30 → 20 → 15 → 10';
  } else if (currentTarget === 20) {
    title = '🔥 Find Bonus TRAKs in order: 20 → 15 → 10';
  } else if (currentTarget === 15) {
    title = '🔥 Find Bonus TRAKs in order: 15 → 10';
  } else if (currentTarget === 10) {
    title = isFinalGamble 
      ? '🔥 ONE CHANCE TO WIN 10 TRAKS ONLY'
      : '🔥 STILL 10 TRAKS TO FIND';
  } else {
    title = `🔥 Find Bonus TRAKs: ${availableBonuses.join(' → ')}`;
  }

  return (
    <div className="bonus-tracker">
      <div className="bonus-title">
        {title}
      </div>
      <div className="bonus-progress">
        {availableBonuses.map((bonus, index) => {
          const currentIndex = availableBonuses.indexOf(currentTarget);
          let state = 'pending';
          
          if (index < currentIndex) {
            state = 'completed';
          } else if (index === currentIndex) {
            state = 'current';
          }
          
          return (
            <div key={bonus} className={`bonus-item ${state}`}>
              {state === 'completed' && '✅'}
              {state === 'current' && '🔥'}
              {state === 'pending' && '❓'}
              {bonus}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// 🔧 FIX 2026.01.18: AdBanner fetches from /api/global-adverts for HTML ads
// ============================================================================
const AdBanner = ({ selectedGame, squares }) => {
  const [imageError, setImageError] = useState(false);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Calculate revealed count for impression key - changes on EVERY cell click
  const revealedCount = squares?.filter(sq => sq?.revealed).length || 0;
  
  // Fetch global advert settings on mount
  useEffect(() => {
    const fetchGlobalSettings = async () => {
      try {
        const response = await fetch('https://api.traktag.com/api/global-adverts');
        if (response.ok) {
          const data = await response.json();
          console.log('AdBanner: Loaded settings from /api/global-adverts', data);
          setGlobalSettings(data);
        }
      } catch (error) {
        console.error('AdBanner: Failed to load global settings', error);
      } finally {
        setSettingsLoaded(true);
      }
    };
    fetchGlobalSettings();
  }, []);
  
  // Trigger AdSense when HTML ad is loaded
  useEffect(() => {
    if (globalSettings?.mobile_banner_html?.includes('adsbygoogle')) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        console.log('AdBanner: Pushed AdSense ad');
      } catch (e) {
        console.error('AdBanner: AdSense push error', e);
      }
    }
  }, [globalSettings]);
  
  // Reset error state when URL changes
  useEffect(() => {
    setImageError(false);
  }, [selectedGame?.mobileBannerUrl]);
  
  // Priority order:
  // 1. Game-specific HTML (selectedGame.mobileBannerHtml)
  // 2. Game-specific image URL (selectedGame.mobileBannerUrl)
  // 3. Global HTML (mobile_banner_html from /api/global-adverts)
  // 4. Global image URL (mobile_banner_url from /api/global-adverts)
  // 5. Placeholder
  
  const clickUrl = globalSettings?.global_click_url || selectedGame?.adClickUrl || 'https://traktag.com/advertise';
  
  // Priority 1: Game-specific HTML
  if (selectedGame?.mobileBannerHtml) {
    return (
      <div 
        className="ad-container"
        style={{
          width: '340px',
          margin: '0 auto',
          backgroundColor: '#1a1a2e',
          padding: '4px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: selectedGame.mobileBannerHtml }} />
      </div>
    );
  }
  
  // Priority 2: Game-specific image URL
  if (selectedGame?.mobileBannerUrl && !imageError) {
    return (
      <div 
        className="ad-container"
        style={{
          width: '340px',
          margin: '0 auto',
          backgroundColor: '#1a1a2e',
          padding: '4px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <a 
          href={clickUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="ad-link"
          style={{display: 'block', textDecoration: 'none'}}
          onClick={(e) => e.stopPropagation()}
        >
          <img 
            key={`picture-grid-banner-${revealedCount}`}
            src={selectedGame.mobileBannerUrl} 
            alt="Advertisement"
            style={{
              width: '340px',
              height: '50px', 
              objectFit: 'cover', 
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              display: 'block'
            }}
            onError={() => setImageError(true)}
          />
        </a>
      </div>
    );
  }
  
  // Priority 3: Global HTML (AdSense)
  if (globalSettings?.mobile_banner_html) {
    return (
      <div 
        className="ad-container"
        style={{
          width: '100%',
          backgroundColor: '#1a1a2e',
          padding: '4px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: globalSettings.mobile_banner_html }} />
      </div>
    );
  }
  
  // Priority 4: Global image URL
  if (globalSettings?.mobile_banner_url) {
    return (
      <div 
        className="ad-container"
        style={{
          width: '340px',
          margin: '0 auto',
          backgroundColor: '#1a1a2e',
          padding: '4px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <a 
          href={clickUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="ad-link"
          style={{display: 'block', textDecoration: 'none'}}
          onClick={(e) => e.stopPropagation()}
        >
          <img 
            src={globalSettings.mobile_banner_url} 
            alt="Advertisement"
            style={{
              width: '340px',
              height: '50px', 
              objectFit: 'cover', 
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              display: 'block'
            }}
          />
        </a>
      </div>
    );
  }
  
  // Priority 5: Placeholder (only after settings loaded)
  if (settingsLoaded) {
    return (
      <div 
        className="ad-container"
        style={{
          width: '340px',
          margin: '0 auto',
          backgroundColor: '#1a1a2e',
          padding: '4px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
      >
        <a 
          href={clickUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{display: 'block', textDecoration: 'none'}}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            style={{
              width: '340px',
              height: '50px',
              background: 'linear-gradient(to right, #4A5568, #2D3748)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            📺 Advertise Here
          </div>
        </a>
      </div>
    );
  }
  
  // Still loading - show nothing
  return null;
};

// Champagne Celebration Component
const ChampagneCelebration = ({ isPlayerGift, isTenBonus, isCommiseration }) => {
  const [visible, setVisible] = useState(true);

  console.log('🎭 ChampagneCelebration rendering:', { isPlayerGift, isTenBonus, isCommiseration, visible });

  useEffect(() => {
    console.log('🎭 Setting auto-hide timer');
    const timer = setTimeout(() => {
      console.log('🎭 Auto-hiding celebration bubble');
      setVisible(false);
    }, isCommiseration ? 2500 : 2500); // 🔧 FIX 2025.11.27.1600: Extended from 1s to 2.5s for readability

    return () => {
      console.log('🎭 Cleaning up celebration timer');
      clearTimeout(timer);
    };
  }, [isCommiseration]);

  useEffect(() => {
    const backupTimer = setTimeout(() => {
      console.log('🎭 Backup force-hiding celebration bubble');
      setVisible(false);
    }, isCommiseration ? 3000 : 3500); // 🔧 FIX 2025.11.27.1600: Extended backup timer

    return () => clearTimeout(backupTimer);
  }, [isCommiseration]);

  if (!visible) {
    console.log('🎭 Celebration bubble not visible, returning null');
    return null;
  }

  let message = '';
  let className = 'celebration-bubble';  // 🔧 FIX 2025.11.27: Was 'celebration-message', CSS uses 'celebration-bubble'

  if (isCommiseration) {
    message = '💔 SORRY YOU LOST YOUR\nTEN TRAK BONUS THIS TIME';
    className += ' commiseration';  // 🔧 FIX 2025.11.27: Was 'commiseration-celebration', CSS uses 'commiseration'
  } else if (isTenBonus) {
    message = '🎰 JACKPOT! 🥂\n+10 TRAKS!';
    className += ' jackpot-celebration';
  } else if (isPlayerGift) {
    message = '🎁 SPECIAL BONUS AWARD! 🥂\n+50 TRAKS!'; // ✏️ UPDATED: Changed from 100 to 50
    className += ' gift-celebration';
  } else {
    message = '🎉 CHAMPAGNE MOMENT! 🥂\n+50 TRAKS!'; // ✏️ UPDATED: Changed from 100 to 50
  }

  return (
    <div className={className}>
      {message.split('\n').map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </div>
  );
}

export default PictureGridGame;
